/**
 * Offscreen video pre-buffering pool.
 *
 * Maintains a small pool of `<video>` elements (not attached to DOM) with
 * `src` set and `preload='auto'`. When they reach `canplay`, the browser has
 * the bytes decoded and cached. When Cornerstone later requests the same URL
 * via `VideoViewport.setVideoURL()`, the browser serves from HTTP cache and
 * `loadedmetadata` fires near-instantly.
 *
 * LRU eviction keeps memory bounded (MAX_POOL_SIZE videos).
 */

interface BufferedVideo {
  element: HTMLVideoElement;
  url: string;
  dicomUuid: string;
  timestamp: number;
}

const MAX_POOL_SIZE = 6;
const pool = new Map<string, BufferedVideo>();

export const videoBufferPool = {
  /**
   * Pre-buffer a video for a DICOM. Creates an offscreen `<video>` element
   * that loads the video bytes into browser memory/cache.
   */
  preload(dicomUuid: string, url: string): void {
    if (pool.has(dicomUuid)) {
      // Update timestamp for LRU (recently accessed = keep longer)
      const existing = pool.get(dicomUuid)!;
      existing.timestamp = Date.now();
      return;
    }

    // Evict oldest if at capacity
    if (pool.size >= MAX_POOL_SIZE) {
      let oldestKey: string | null = null;
      let oldestTime = Infinity;
      pool.forEach((entry, key) => {
        if (entry.timestamp < oldestTime) {
          oldestTime = entry.timestamp;
          oldestKey = key;
        }
      });
      if (oldestKey) {
        const oldest = pool.get(oldestKey);
        if (oldest) {
          oldest.element.pause();
          oldest.element.removeAttribute('src');
          oldest.element.load();
          pool.delete(oldestKey);
        }
      }
    }

    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    // Not attached to DOM — holds decoded bytes in memory only

    const entry: BufferedVideo = {
      element: video,
      url,
      dicomUuid,
      timestamp: Date.now(),
    };

    video.src = url;

    pool.set(dicomUuid, entry);
  },

  /** Check if the video for this DICOM has decoded bytes available.
   *  Uses the live `readyState` instead of a cached flag — if the browser
   *  garbage-collected decoded data during idle, this returns false and
   *  the preloader will re-buffer the video. */
  isReady(dicomUuid: string): boolean {
    const entry = pool.get(dicomUuid);
    if (!entry) return false;
    // HAVE_FUTURE_DATA (3) or HAVE_ENOUGH_DATA (4) = decoded bytes available
    return entry.element.readyState >= 3;
  },

  /** Get the pre-buffered URL for a DICOM, or null if not in pool. */
  getUrl(dicomUuid: string): string | null {
    return pool.get(dicomUuid)?.url ?? null;
  },

  /** Release a single video from the pool and free its memory. */
  evict(dicomUuid: string): void {
    const entry = pool.get(dicomUuid);
    if (entry) {
      entry.element.pause();
      entry.element.removeAttribute('src');
      entry.element.load(); // Release network/memory resources
      pool.delete(dicomUuid);
    }
  },

  /** Release all videos from the pool. */
  clear(): void {
    pool.forEach((entry) => {
      entry.element.pause();
      entry.element.removeAttribute('src');
      entry.element.load();
    });
    pool.clear();
  },

  /** Current pool size (for diagnostics). */
  get size(): number {
    return pool.size;
  },
};
